import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useDbNotifications } from '@/hooks/useDbNotifications';
import { useLanguage } from '@/hooks/useLanguage';

const NotificationIcon: React.FC = () => {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useDbNotifications();
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);

  const toggleDropdown = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  // Format date to relative time (e.g., "2 hours ago")
  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffDay > 0) {
      return diffDay === 1 ? `${diffDay} ${t('day')} ago` : `${diffDay} days ago`;
    }
    if (diffHour > 0) {
      return diffHour === 1 ? `${diffHour} hour ago` : `${diffHour} hours ago`;
    }
    if (diffMin > 0) {
      return diffMin === 1 ? `${diffMin} minute ago` : `${diffMin} minutes ago`;
    }
    return 'Just now';
  };

  const handleNotificationClick = (notificationId: string, link?: string) => {
    markAsRead(notificationId);
    setIsOpen(false);
    
    // If there's a link, handle it in the parent component
    if (link && link.startsWith('/')) {
      // Internal link - will be handled by React Router
      return;
    } else if (link) {
      // External link - open in new tab
      window.open(link, '_blank');
    }
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = () => {
      if (isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isOpen]);

  // Get icon for notification type
  const getNotificationIcon = (type?: string, customIcon?: string) => {
    if (customIcon) {
      return (
        <i className={`${customIcon}`}></i>
      );
    }

    switch (type) {
      case 'announcement':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M18 3a1 1 0 00-1.447-.894L8.763 6H5a3 3 0 000 6h.28l1.771 5.316A1 1 0 008 18h1a1 1 0 001-1v-4.382l6.553 3.276A1 1 0 0018 15V3z" clipRule="evenodd" />
          </svg>
        );
      case 'alert':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        );
      case 'course':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
          </svg>
        );
      case 'system':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M2 5a2 2 0 012-2h12a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V5zm3.293 1.293a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 01-1.414-1.414L7.586 10 5.293 7.707a1 1 0 010-1.414zM11 12a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
          </svg>
        );
      case 'general':
      default:
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
          </svg>
        );
    }
  };

  // Get background color for notification type
  const getNotificationBgColor = (type?: string) => {
    switch (type) {
      case 'announcement':
        return 'bg-blue-500';
      case 'alert':
        return 'bg-red-500';
      case 'course':
        return 'bg-green-500';
      case 'system':
        return 'bg-purple-500';
      case 'general':
      default:
        return 'bg-gray-500';
    }
  };

  return (
    <div className="relative">
      <button
        onClick={toggleDropdown}
        className="w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 bg-white/10 backdrop-blur-xl hover:bg-white/20 border border-white/20 relative"
        title={t('notifications')}
        aria-label={t('notifications')}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5 text-white"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
        </svg>
        
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-semibold leading-none text-white bg-red-500 rounded-full transform -translate-y-1/3 translate-x-1/3">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full mt-1 right-0 rtl:right-auto rtl:left-0 bg-black/80 backdrop-blur-xl rounded-xl shadow-xl border border-white/10 p-2 min-w-[320px] max-w-[360px] z-50 max-h-[70vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute top-[-8px] right-3 rtl:right-auto rtl:left-3 w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-b-[8px] border-b-black/80"></div>
            
            <div className="px-3 py-2 border-b border-white/10 mb-2 flex justify-between items-center">
              <h3 className="text-white font-medium">
                {t('notifications')} {unreadCount > 0 && `(${unreadCount} new)`}
              </h3>
              {unreadCount > 0 && (
                <button 
                  onClick={() => markAllAsRead()}
                  className="text-xs text-blue-400 hover:text-blue-300"
                >
                  {t('markAllAsRead')}
                </button>
              )}
            </div>

            {/* List of notifications */}
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="text-center py-6 text-white/60">
                  <p>{t('noNotifications')}</p>
                </div>
              ) : (
                notifications.slice(0, 5).map((notification) => {
                  const NotificationWrapper = notification.link ? Link : 'div';
                  const wrapperProps = notification.link && notification.link.startsWith('/') 
                    ? { to: notification.link } 
                    : {};
                    
                  return (
                    <div
                      key={notification.id}
                      className={`px-3 py-2 rounded-lg hover:bg-white/5 transition-colors cursor-pointer ${!notification.read ? 'bg-white/10' : 'bg-transparent'}`}
                      onClick={() => handleNotificationClick(notification.id, notification.link)}
                    >
                      <div className="flex items-start space-x-3 rtl:space-x-reverse">
                        <div className={`flex-shrink-0 w-8 h-8 ${getNotificationBgColor(notification.type)} rounded-full flex items-center justify-center text-white`}>
                          {getNotificationIcon(notification.type, notification.icon)}
                        </div>
                        <div className="flex-grow">
                          <h4 className={`text-sm font-medium ${!notification.read ? 'text-white' : 'text-white/80'}`}>
                            {notification.title}
                          </h4>
                          <p className="text-white/70 text-xs mt-1 line-clamp-2">
                            {notification.content}
                          </p>
                          <div className="flex justify-between items-center mt-1">
                            <span className="text-white/50 text-xs">
                              {formatRelativeTime(notification.created_at)}
                            </span>
                            {notification.sender_name && (
                              <span className="text-white/50 text-xs">
                                {notification.sender_name}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* View all link */}
            {notifications.length > 5 && (
              <div className="pt-2 mt-2 border-t border-white/10 text-center">
                <Link
                  to="/notifications"
                  className="text-blue-400 hover:text-blue-300 text-sm"
                  onClick={() => setIsOpen(false)}
                >
                  {t('viewAllNotifications')}
                </Link>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationIcon;